import {
  AppLauncherConfig,
  DetectedDevice,
  LauncherFontFamily,
  LauncherIconCustomLink,
  LaunchLinkType,
  ProfileLauncherConfig,
  SocialLinkType,
} from "@store-platform/types"
import { FC, SVGProps } from "react"
import {
  SvgAndroidRobot,
  SvgAward,
  SvgBadgeDollarSign,
  SvgBadgeEuro,
  SvgBrowser,
  SvgCamera,
  SvgCoffee,
  SvgDiscordLogo,
  SvgFacebookLogo,
  SvgFileText,
  SvgGame,
  SvgGift,
  SvgGithubLogo,
  SvgGlobe,
  SvgGooglePlay,
  SvgGraduationCap,
  SvgHeadset,
  SvgHelpCircle,
  SvgInfo,
  SvgInstagram,
  SvgIos,
  SvgKeyRound,
  SvgLandmark,
  SvgLinkedin,
  SvgMacOs,
  SvgMail,
  SvgMapPin,
  SvgMessagesSquare,
  SvgPawPrint,
  SvgPhone,
  SvgReviewSourceAppStore,
  SvgReviewSourcePlayStore,
  SvgReviewSourceStoredotapp,
  SvgReviewSourceTrustPilot,
  SvgSnapchat,
  SvgSparkles,
  SvgStar,
  SvgTelegram,
  SvgThumbsUp,
  SvgTiktokLogo,
  SvgWandSparkles,
  SvgXLogo,
  SvgYoutube,
  SvgYoutubeLogo,
} from "@store-platform/ui/icons"

export const LaunchLinkIcon: Record<
  LaunchLinkType,
  FC<SVGProps<SVGSVGElement>>
> = {
  "install.web_app": SvgBrowser,
  "install.android": SvgGooglePlay,
  "direct_install.android": SvgAndroidRobot,
  "install.ios": SvgIos,
  "install.macos": SvgMacOs,
  "direct_install.macos": SvgMacOs,
}

export const LaunchLinkLabel: Record<LaunchLinkType, string> = {
  "install.web_app": "Launch App in your Browser",
  "install.android": "Get it on Google Play",
  "direct_install.android": "Download App for Android (.apk)",
  "install.ios": "Download on the App Store",
  "install.macos": "Download on the Mac App Store",
  "direct_install.macos": "Download App for Mac (.dmg)",
}

export const LaunchLinkAction: Record<LaunchLinkType, "open" | "download"> = {
  "install.web_app": "open",
  "install.android": "open",
  "direct_install.android": "download",
  "install.ios": "open",
  "install.macos": "open",
  "direct_install.macos": "download",
}

export const SocialLinkIcon: Record<
  SocialLinkType,
  FC<SVGProps<SVGSVGElement>>
> = {
  "social.twitter": SvgXLogo,
  "social.instagram": SvgInstagram,
  "social.linkedin": SvgLinkedin,
  "social.facebook": SvgFacebookLogo,
  "social.discord": SvgDiscordLogo,
  "social.tiktok": SvgTiktokLogo,
  "social.telegram": SvgTelegram,
  "social.snapchat": SvgSnapchat,
  "social.youtube": SvgYoutubeLogo,
  "social.github": SvgGithubLogo,
  "social.email": SvgMail,
  "social.website": SvgGlobe,
}

export const SocialLinkLabel: Record<SocialLinkType, string> = {
  "social.twitter": "X",
  "social.instagram": "Instagram",
  "social.linkedin": "LinkedIn",
  "social.facebook": "Facebook",
  "social.discord": "Discord",
  "social.tiktok": "TikTok",
  "social.telegram": "Telegram",
  "social.snapchat": "Snapchat",
  "social.youtube": "YouTube",
  "social.github": "GitHub",
  "social.email": "Email",
  "social.website": "Website",
}

export const DeviceLabels: Record<DetectedDevice, string> = {
  unknown: "Unknown Device",
  ios: "iOS",
  ipados: "iPadOS",
  macos: "macOS",
  android: "Android",
  windows: "Windows",
  linux: "Linux",
}

export const ReviewSourceLabels: Record<string, string> = {
  store_app: "Store.app",
  play_store: "Google Play",
  app_store_ios: "iOS App Store",
  app_store_mac: "Mac App Store",
  trust_pilot: "TrustPilot",
}

export const ReviewSourceIcons: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  store_app: SvgReviewSourceStoredotapp,
  play_store: SvgReviewSourcePlayStore,
  app_store_ios: SvgReviewSourceAppStore,
  app_store_mac: SvgReviewSourceAppStore,
  trust_pilot: SvgReviewSourceTrustPilot,
}

export const DevicePreviewConfig: Record<
  string,
  {
    label: string
    device: DetectedDevice
    maxSize?: { width: number; height: number }
  }
> = {
  unknown: {
    label: "Unknown Device",
    device: "unknown",
  },
  iPhone: {
    label: "iPhone",
    device: "ios",
    maxSize: { width: 430, height: 830 },
  },
  iPad: {
    label: "iPad",
    device: "ios",
    maxSize: { width: 1280, height: 800 },
  },
  androidPhone: {
    label: "Android Phone",
    device: "android",
    maxSize: { width: 430, height: 830 },
  },
  androidTablet: {
    label: "Android Tablet",
    device: "android",
    maxSize: { width: 1280, height: 800 },
  },
  macos: {
    label: "Mac",
    device: "macos",
  },
  chromebook: {
    label: "Chromebook",
    device: "android",
  },
}

export const LauncherFontFamilies: ReadonlyArray<LauncherFontFamily> =
  Array.from(
    new Set<LauncherFontFamily>([
      "Roboto",
      "Open Sans",
      "Poppins",
      "Montserrat",
      "Lato",
      "Oswald",
      "Raleway",
      "Merriweather",
      "Playfair Display",
      "Inter",
    ]),
  )

export function linkMatchDevice(
  linkType: LaunchLinkType,
  device?: DetectedDevice | undefined,
): boolean {
  if (!device) return false

  switch (linkType) {
    case "install.android":
    case "direct_install.android":
      return device === "android"
    case "install.ios":
      return device === "ios"
    case "install.macos":
    case "direct_install.macos":
      return device === "macos"
    default:
      return false
  }
}

export const DefaultAppLauncherConfig: Pick<
  AppLauncherConfig,
  "iconUrl" | "name" | "tagline" | "rating" | "styles" | "settings"
> = {
  iconUrl: "https://static.store.app/platform/launch/placeholder-icon.jpg",
  name: "",
  tagline: "",
  rating: {
    enabled: false,
  },
  styles: {
    colors: {
      canvas: "#f5f6f9",
      box: "#fff",
      primary: "#111827",
      secondary: "#f9fafb",
      mainText: "#333",
      primaryLinkText: "#fff",
      secondaryLinkText: "#111827",
      boxShadow: "#333",
      taglineText: "#333",
    },
    typography: { fontFamily: "Roboto" },
    layout: { outerRadius: 20, linkRadius: 12 },
  },
  settings: {},
}

export const DefaultProfileLauncherConfig: Pick<
  ProfileLauncherConfig,
  "profilePicture" | "displayName" | "tagline" | "rating" | "styles"
> = {
  profilePicture:
    "https://static.store.app/platform/launch/placeholder-icon.jpg",
  displayName: "",
  tagline: "",
  rating: {
    enabled: false,
  },
  styles: {
    colors: {
      canvas: "#f5f6f9",
      box: "#fff",
      primary: "#111827",
      secondary: "#f9fafb",
      mainText: "#333",
      primaryLinkText: "#fff",
      secondaryLinkText: "#111827",
      boxShadow: "#333",
      taglineText: "#333",
    },
    typography: { fontFamily: "Roboto" },
    layout: { outerRadius: 20, linkRadius: 12 },
  },
}

export const LauncherCustomLinkIcons: Record<
  LauncherIconCustomLink["icon"],
  { label: string; icon: FC<SVGProps<SVGSVGElement>> }
> = {
  award: { label: "Award", icon: SvgAward },
  badgeDollarSign: { label: "Dollar Sign", icon: SvgBadgeDollarSign },
  badgeEuro: { label: "Euro", icon: SvgBadgeEuro },
  camera: { label: "Camera", icon: SvgCamera },
  circleHelp: { label: "Help", icon: SvgHelpCircle },
  circleInfo: { label: "Info", icon: SvgInfo },
  coffee: { label: "Coffee", icon: SvgCoffee },
  fileText: { label: "File Text", icon: SvgFileText },
  gamepad: { label: "Gamepad", icon: SvgGame },
  gift: { label: "Gift", icon: SvgGift },
  globe: { label: "Globe", icon: SvgGlobe },
  graduationCap: { label: "Graduation Cap", icon: SvgGraduationCap },
  headset: { label: "Headset", icon: SvgHeadset },
  keyRound: { label: "Key", icon: SvgKeyRound },
  landmark: { label: "Landmark", icon: SvgLandmark },
  mail: { label: "Mail", icon: SvgMail },
  mapPin: { label: "Map Pin", icon: SvgMapPin },
  messagesSquare: { label: "Messages", icon: SvgMessagesSquare },
  pawPrint: { label: "Paw Print", icon: SvgPawPrint },
  phone: { label: "Phone", icon: SvgPhone },
  sparkles: { label: "Sparkles", icon: SvgSparkles },
  star: { label: "Star", icon: SvgStar },
  thumbsUp: { label: "Thumbs Up", icon: SvgThumbsUp },
  wandSparkles: { label: "Wand Sparkles", icon: SvgWandSparkles },
  youtube: { label: "YouTube", icon: SvgYoutube },
}
